import { useGetLabelList } from '@modusign/openapi-client-category';
import { useReducer, useState } from 'react';
import { useDebounce } from 'react-use';

import validateName from '@app/features/label/libs/validateName';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';

const errorMessage: Record<'EMPTY' | 'INVALID', string> = {
  EMPTY: '이름은 필수입니다.',
  INVALID: '~ ! # $ % ^ & * + - = ? _ ( ) { } [ ] . ‘ 이용 가능',
};

interface ReducerState {
  name: string;
  description: string;
  nameErrorMessage: string;
  isDuplicated: boolean;
}

type Action =
  | { type: 'setName'; value: string }
  | { type: 'setDescription'; value: string }
  | { type: 'setDuplication' };

const reducer = (state: ReducerState, action: Action): ReducerState => {
  switch (action.type) {
    case 'setDescription':
      return { ...state, description: action.value };
    case 'setDuplication':
      return { ...state, isDuplicated: true };
    case 'setName': {
      const error = validateName(action.value.trim());
      return {
        ...state,
        name: action.value,
        isDuplicated: false,
        nameErrorMessage: error ? errorMessage[error] : '',
      };
    }
    default:
      return state;
  }
};

export default function useLabelBasicInfoForm(options?: {
  initialName: string;
  initialDescription: string;
  labelId?: string;
}): {
  name: string;
  description: string;
  nameErrorMessage: string;
  isDuplicated: boolean;
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
} {
  const [{ description, isDuplicated, name, nameErrorMessage }, dispatch] = useReducer(reducer, {
    name: options?.initialName || '',
    description: options?.initialDescription || '',
    nameErrorMessage: '',
    isDuplicated: false,
  });

  useCheckLabelNameDuplication({
    name: name.trim(),
    labelId: options?.labelId,
    onDuplicate: () => dispatch({ type: 'setDuplication' }),
  });

  return {
    name,
    description,
    isDuplicated,
    nameErrorMessage,
    onNameChange: (value) => dispatch({ type: 'setName', value }),
    onDescriptionChange: (value) => dispatch({ type: 'setDescription', value }),
  };
}

function useCheckLabelNameDuplication({
  name,
  labelId,
  onDuplicate,
}: {
  name: string;
  labelId?: string;
  onDuplicate: () => void;
}) {
  const workspaceId = useWorkspaceId();

  const [debouncedName, setDebouncedName] = useState(name);
  useDebounce(
    () => {
      setDebouncedName(name);
    },
    500,
    [name]
  );

  useGetLabelList(
    workspaceId,
    { name: debouncedName, offset: 0, limit: 100 },
    {
      query: {
        onSuccess: (data) => {
          if (data.labels.find((label) => label.name === debouncedName && label.id !== labelId)) {
            onDuplicate();
          }
        },
        enabled: !!debouncedName,
      },
    }
  );
}
